import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useCreateDag, useUpdateDag } from "api/api-hooks/airflow-hooks";
import {
  ControllerNumberInput,
  ControllerTextInput, ControllerToggleInput,
} from "components/basic/controller-inputs/ControllerInputs";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import useCurrentCompanyContext from "hooks/useCurrentCompanyContext";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import {
  Controller,
  useForm
} from "react-hook-form";
import { T } from "services/lang/LanguageService";
import { AirflowDag, AirflowDagDto } from "types/airflow";
import { SIMPLE_IDENTIFIER_REGEX } from "utils/regex";
import { z } from "zod";

const schema = z.object({
  dagId: z.string().regex(SIMPLE_IDENTIFIER_REGEX, "Airflow id must be alphanumeric with underscores"),
  title: z.string(),
  description: z.optional(z.string()),
  cronSchedule: z.string(),
  timeout: z.number(),
  useStaging: z.boolean(),
});

type AirflowDagFormProps = {
  closeDialog: () => void;
  dag?: AirflowDag;
};

const AirflowDagForm: React.FC<AirflowDagFormProps> = ({ closeDialog, dag }) => {
  const company = useCurrentCompanyContext();

  const createDag = useCreateDag();
  const updateDag = useUpdateDag();

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<AirflowDagDto>({
    values: dag && { ...dag, companyRef: company?.companyReference ?? "" },
    resolver: zodResolver(schema),
  });

  const onSubmit = (data: AirflowDagDto) => {
    const dagDto: AirflowDagDto = {
      dagId: data.dagId,
      title: data.title,
      description: data.description,
      cronSchedule: data.cronSchedule,
      timeout: data.timeout,
      companyRef: company?.companyReference ?? "",
      useStaging: data.useStaging ?? false,
    };

    if (!dag) {
      createDag.mutate(dagDto, { onSuccess: closeDialog });
    } else {
      updateDag.mutate(dagDto, { onSuccess: closeDialog });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-wrap">
        <Controller
          name="dagId"
          control={control}
          render={({ field }) => (
            <ControllerTextInput field={field} errors={errors} label="Airflow ID" />
          )}
        />
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <ControllerTextInput field={field} errors={errors} label={T("word.title")} />
          )}
        />
      </div>
      <div className="flex flex-wrap">
        <InputLabelWrapper labelName={T("word.company")} col={3}>
          <InputText value={company?.title} className="p-inputtext-sm" disabled readOnly />
        </InputLabelWrapper>
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <ControllerTextInput
              col={9}
              field={field}
              errors={errors}
              label={T("word.description")}
            />
          )}
        />
      </div>
      <div className="flex flex-wrap">
        <Controller
          defaultValue="*/5 * * * *"
          name="cronSchedule"
          control={control}
          render={({ field }) => (
            <ControllerTextInput field={field} errors={errors} label={T("word.schedule")} />
          )}
        />
        <Controller
          defaultValue={600}
          name="timeout"
          control={control}
          render={({ field }) => (
            <ControllerNumberInput
              col={4}
              field={field}
              errors={errors}
              label={T("word.timeout")}
              suffix=" s"
            />
          )}
        />

        <Controller
          defaultValue={false}
          name="useStaging"
          control={control}
          render={({ field }) => (
            <ControllerToggleInput
              col={2}
              field={field}
              errors={errors}
              label={T("word.use-staging")}
            />
          )}
        />
      </div>
      <div className="flex justify-content-end gap-2 mx-2">
        <Button
          label={T("action.cancel")}
          className="p-button-sm p-button-outlined p-button-secondary"
          type="button"
          onClick={closeDialog}
        />
        {!dag && (
          <Button
            label={T("action.create")}
            className="p-button-sm p-button-success"
            loading={createDag.isPending}
          />
        )}
        {dag && (
          <Button
            label={T("action.update")}
            className="p-button-sm p-button-success"
            loading={updateDag.isPending}
          />
        )}
      </div>
    </form>
  );
};

export default AirflowDagForm;